import Phaser from 'phaser';
import type { ThemeKey } from '../types';

export function makeFence(
  scene: Phaser.Scene,
  x: number,
  groundY: number,
  themeKey?: ThemeKey,
): Phaser.GameObjects.Container {
  if (themeKey === 'desert') {
    // A round hay bale on the ground.
    const c = scene.add.container(x, groundY - 24);
    // Bale body (golden cylinder side-on)
    c.add(scene.add.ellipse(0, 6, 70, 44, 0xd9b56a));
    c.add(scene.add.ellipse(0, 6, 64, 38, 0xeac98a));
    // Spiral end visible on a bale
    c.add(scene.add.ellipse(-26, 6, 14, 38, 0xc89849));
    c.add(scene.add.ellipse(-26, 6, 8, 30, 0xeac98a));
    c.add(scene.add.ellipse(-26, 6, 4, 22, 0xc89849));
    // Strands of hay across the body
    const g = scene.add.graphics();
    g.lineStyle(1.2, 0xa07827, 0.7);
    for (let i = -22; i <= 24; i += 6) {
      g.beginPath();
      g.moveTo(i, -12);
      g.lineTo(i + 2, 22);
      g.strokePath();
    }
    c.add(g);
    // A few stray bits of hay sticking out
    c.add(scene.add.rectangle(-32, -2, 6, 1.5, 0xc89849));
    c.add(scene.add.rectangle(34, 4, 6, 1.5, 0xc89849));
    c.add(scene.add.rectangle(0, -16, 1.5, 4, 0xa07827));
    return c;
  }
  if (themeKey === 'mountains' || themeKey === 'snowy') {
    // A snow mound to jump over.
    const c = scene.add.container(x, groundY - 20);
    // Base wide drift
    c.add(scene.add.ellipse(0, 14, 80, 22, 0xeaf2fa));
    // Main mound
    c.add(scene.add.ellipse(0, 0, 64, 42, 0xffffff));
    c.add(scene.add.ellipse(-10, -2, 32, 30, 0xeaf2fa));
    c.add(scene.add.ellipse(14, 4, 28, 24, 0xffffff));
    // Highlight
    c.add(scene.add.ellipse(-4, -10, 14, 6, 0xffffff));
    // Tiny sparkles
    c.add(scene.add.circle(-22, -2, 1, 0xc4d0e0));
    c.add(scene.add.circle(20, -6, 1, 0xc4d0e0));
    c.add(scene.add.circle(0, -14, 1, 0xc4d0e0));
    return c;
  }
  if (themeKey === 'house') {
    // House level: a toy on the floor to dodge.
    const c = scene.add.container(x, groundY - 20);
    // Pick one of three toy types based on x position so spawned obstacles vary
    const variant = Math.floor(x / 250) % 3;
    if (variant === 0) {
      // Lego brick
      c.add(scene.add.rectangle(0, 8, 44, 18, 0xc23b3b));
      c.add(scene.add.circle(-12, -2, 6, 0xc23b3b));
      c.add(scene.add.circle(0, -2, 6, 0xc23b3b));
      c.add(scene.add.circle(12, -2, 6, 0xc23b3b));
      c.add(scene.add.circle(-12, -3, 3, 0xe06868));
      c.add(scene.add.circle(0, -3, 3, 0xe06868));
      c.add(scene.add.circle(12, -3, 3, 0xe06868));
    } else if (variant === 1) {
      // Toy ball
      c.add(scene.add.circle(0, 6, 16, 0x4a8acc));
      c.add(scene.add.rectangle(0, 6, 32, 4, 0xffd24a));
      c.add(scene.add.rectangle(0, 6, 4, 32, 0xffd24a));
      c.add(scene.add.circle(-5, 1, 4, 0x88c0e8, 0.6));
    } else {
      // Toy car
      c.add(scene.add.rectangle(0, 8, 50, 12, 0x3a8a3a));
      c.add(scene.add.rectangle(0, 0, 32, 12, 0x88c0e8));
      c.add(scene.add.rectangle(-7, -2, 10, 8, 0xb8e0f4, 0.7));
      c.add(scene.add.rectangle(7, -2, 10, 8, 0xb8e0f4, 0.7));
      c.add(scene.add.circle(-15, 14, 6, 0x222222));
      c.add(scene.add.circle(15, 14, 6, 0x222222));
    }
    return c;
  }
  if (themeKey === 'river') {
    // Ocean / underwater: a clump of tall seaweed you have to jump over.
    const c = scene.add.container(x, groundY - 30);
    // Sandy mound at the base
    c.add(scene.add.ellipse(0, 24, 60, 14, 0xc4a872));
    // Seaweed strands, layered light + dark green
    const draw = scene.add.graphics();
    draw.fillStyle(0x1f6b48, 1);
    for (const sx of [-18, -6, 6, 18]) {
      draw.fillTriangle(sx - 4, 24, sx + 4, 24, sx + Math.sin(sx * 0.5) * 6, -28);
    }
    draw.fillStyle(0x4ea36b, 1);
    for (const sx of [-12, 0, 12]) {
      draw.fillTriangle(sx - 3, 24, sx + 3, 24, sx + Math.sin(sx * 0.7) * 8, -36);
    }
    // Highlight blades
    draw.fillStyle(0xa6d29a, 1);
    draw.fillRect(-12, -20, 1.5, 30);
    draw.fillRect(2, -28, 1.5, 38);
    draw.fillRect(14, -16, 1.5, 26);
    c.add(draw);
    // A few bubbles drifting up
    c.add(scene.add.circle(-8, -38, 2, 0xffffff, 0.7));
    c.add(scene.add.circle(8, -32, 2, 0xffffff, 0.7));
    c.add(scene.add.circle(2, -44, 1.5, 0xffffff, 0.6));
    return c;
  }
  const c = scene.add.container(x, groundY - 30);
  c.add([
    scene.add.rectangle(-18, 0, 8, 60, 0x6b3410),
    scene.add.rectangle(18, 0, 8, 60, 0x6b3410),
    scene.add.rectangle(0, -10, 60, 8, 0x8a4a1a),
    scene.add.rectangle(0, 14, 60, 8, 0x8a4a1a),
  ]);
  return c;
}

// Low overhead branch — must DUCK to pass under.
export function makeLowBranch(
  scene: Phaser.Scene,
  x: number,
  groundY: number,
  themeKey: ThemeKey,
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, groundY - 130);
  if (themeKey === 'mountains') {
    // icicle overhang
    c.add(scene.add.rectangle(0, -20, 80, 6, 0x9ea4b5));
    c.add(scene.add.triangle(-20, 0, 0, -10, -10, 18, 10, -10, 0xe7ecf5));
    c.add(scene.add.triangle(10, -2, 0, -10, -8, 14, 8, -10, 0xc4d0e0));
  } else if (themeKey === 'house') {
    // Hanging ceiling light: cord from above, lampshade, glowing bulb.
    // Cord from ceiling
    c.add(scene.add.rectangle(0, -50, 2, 60, 0x3d2914));
    // Lampshade (trapezoid via two stacked ellipses)
    c.add(scene.add.ellipse(0, -10, 40, 8, 0x6b3410));
    c.add(scene.add.ellipse(0, -2, 56, 14, 0xc89849));
    c.add(scene.add.ellipse(0, 2, 56, 6, 0x8a5a2a));
    // Soft warm glow underneath
    c.add(scene.add.ellipse(0, 14, 38, 10, 0xfff1c4, 0.6));
    // Bulb
    c.add(scene.add.circle(0, 12, 6, 0xfff1c4));
    c.add(scene.add.circle(0, 12, 4, 0xffd24a));
  } else if (themeKey === 'river') {
    // hanging vine with leaves
    c.add(scene.add.rectangle(0, -14, 70, 4, 0x357748));
    c.add(scene.add.ellipse(-22, 6, 22, 14, 0x4ea36b));
    c.add(scene.add.ellipse(0, 10, 24, 14, 0x4ea36b));
    c.add(scene.add.ellipse(22, 6, 22, 14, 0x4ea36b));
  } else {
    // tree branch with leaves
    c.add(scene.add.rectangle(0, -16, 80, 8, 0x6b3410));
    c.add(scene.add.circle(-26, -2, 16, 0x3a7d2a));
    c.add(scene.add.circle(0, 4, 18, 0x3a7d2a));
    c.add(scene.add.circle(26, -2, 16, 0x3a7d2a));
  }
  return c;
}
